<template>
  <article
    :id="uuid"
    ref="accordion"
    :class="`sam-accordion ${accordionStateClass} ${accordionTypeClass}`"
  >
    <header
      class="sam-accordion__header"
      :class="{ disabled, 'is-open': isOpen }"
      @click.prevent="handleHeaderClick"
      @keydown.prevent
    >
      <SamIcon
        v-if="icon"
        class="sam-accordion__header-icon"
        :icon="icon"
      />

      <div class="sam-accordion__header-content">
        <div class="sam-accordion__header-title-wrapper">
          <slot name="title-prepend"></slot>
          <h6
            :id="'title-' + uuid"
            ref="accordionTitle"
            class="sam-accordion__header-title"
          >
            {{ title }}
            <SamTooltip
              v-if="hasTitleTooltip && !disabled"
              :message="title"
              position="top"
              :anchor-name="'title-' + uuid"
            />
          </h6>

          <slot
            name="title-append"
            v-bind="slotProps"
          ></slot>
        </div>
        <div
          v-if="description || hasSlot('description')"
          class="sam-accordion__header-description-wrapper"
        >
          <slot
            v-if="hasSlot('description')"
            name="description"
            v-bind="slotProps"
          />
          <p v-else>
            {{ description }}
          </p>
        </div>
      </div>

      <SamIcon
        :class="['sam-accordion__header-chevron', chevronPositionClass]"
        :icon="isOpen ? 'arrow-chevron-up' : 'arrow-chevron-down'"
      />
    </header>
    <section
      v-if="hasSlot('body-visible')"
      class="sam-accordion__visible-info"
    >
      <slot
        name="body-visible"
        v-bind="slotProps"
      />
    </section>
    <main
      ref="contentWrapper"
      class="sam-accordion__content-wrapper"
    >
      <div
        ref="content"
        class="sam-accordion__content"
      >
        <slot v-bind="slotProps" />
      </div>
    </main>
  </article>
</template>

<script>
import { debounce, uuidv4 } from '../../../utils/utils';
import SAMORA_POSITIONS from '../../../utils/constants/samora-positions.enum';
import SAMORA_TYPES from '../../../utils/constants/samora-types.enum';
import SamTooltip from '../SamTooltip/SamTooltip.vue';
import SamIcon from '../SamIcon/SamIcon.vue';

export const ACCORDION_TYPES = SAMORA_TYPES.ACCORDION;

const CHEVRON_POSITION_CLASSES_MAP = {
  [SAMORA_POSITIONS.START]: '--is-start-position',
  [SAMORA_POSITIONS.END]: '--is-end-position',
};

export default {
  name: 'SamAccordion',
  components: {
    SamTooltip,
    SamIcon,
  },
  props: {
    id: {
      type: String,
      required: false,
      default: null,
    },
    icon: {
      type: String,
      required: false,
      default: null,
    },
    title: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      required: false,
      default: '',
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    samType: {
      type: String,
      required: false,
      default: ACCORDION_TYPES.DEFAULT,
    },
    propagateHeaderClick: {
      type: Boolean,
      required: false,
      default: false,
    },
    chevronPosition: {
      type: String,
      required: false,
      default: SAMORA_POSITIONS.END,
      validator: (val) => Object.keys(CHEVRON_POSITION_CLASSES_MAP).includes(val),
    },
  },
  data: () => ({
    isOpen: false,
    resizeObserver: null,
    hasTitleTooltip: false,
  }),
  computed: {
    uuid() {
      return this.id || `accordion__${uuidv4()}`;
    },
    accordionStateClass() {
      return this.isOpen ? '--opened' : '--closed';
    },
    accordionTypeClass() {
      if (this.samType === ACCORDION_TYPES.FLAT) return 'is-flat';
      return 'is-default';
    },
    slotProps() {
      return {
        isOpen: this.isOpen,
      };
    },
    /**
     * For some reason, when using scoped slot properties the $slots verifier doesn't work.
     * So a workaround that was found was to check if the $scopedSlots also existed.
     */
    hasSlot() {
      return (slotName) => !!this.$slots[slotName] || !!this.$scopedSlots[slotName];
    },
    chevronPositionClass() {
      return (
        CHEVRON_POSITION_CLASSES_MAP[this.chevronPosition] ??
        CHEVRON_POSITION_CLASSES_MAP[SAMORA_POSITIONS.END]
      );
    },
  },
  mounted() {
    this.resizeObserver = new ResizeObserver(
      debounce(() => {
        if (this.isOpen) this.updateHeightOpen();
        this.checkTitleOverflow();
      }, 50)
    );
    this.resizeObserver.observe(this.$refs.content);

    setTimeout(this.checkTitleOverflow, 500);
  },
  beforeDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  },
  methods: {
    open($event) {
      this.isOpen = true;
      this.updateHeightOpen();
      this.$emit('open', $event);
    },
    close($event) {
      this.$refs.contentWrapper.style.height = '0';
      this.isOpen = false;
      this.$emit('close', $event);
    },
    handleHeaderClick($event) {
      if (!this.propagateHeaderClick) {
        $event.stopPropagation();
      }

      if (this.isOpen) this.close($event);
      else this.open($event);
    },
    updateHeightOpen() {
      const height = this.$refs.content.clientHeight;
      this.$refs.contentWrapper.style.height = `${height}px`;
    },
    checkTitleOverflow() {
      const titleElement = this.$refs.accordionTitle;
      this.hasTitleTooltip = titleElement.scrollWidth > titleElement.offsetWidth;
    },
  },
};
</script>

<style scoped lang="scss">
.sam-accordion {
  background-color: $pure--white;

  .sam-accordion__header {
    @extend .d-flex, .gap-2;
    cursor: pointer;

    &.disabled {
      pointer-events: none;
      color: $neutral--60;
    }

    &-title {
      flex-shrink: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      margin: 0;
    }

    &-title-wrapper {
      @extend .d-flex, .items-center;
      gap: $size--2;
      flex-grow: 1;
      overflow: hidden;
    }

    &-description-wrapper {
      @extend .text--body-2, .pt-1;
    }

    &-icon {
      flex-shrink: 0;
      &.--is-start-position {
        order: -1;
      }
    }

    &-content {
      flex-grow: 1;
      overflow: hidden;
    }

    &-chevron {
      flex-shrink: 0;
      &.--is-start-position {
        order: -1;
      }
    }
  }

  .sam-accordion__content,
  .sam-accordion__visible-info {
    @extend .text--body-1;
  }

  .sam-accordion__content-wrapper {
    height: 0;
    overflow: hidden;
    transition: height 0.2s ease;
  }

  &.is-default {
    border-radius: $size--1;
    border: $size--0--fourth solid $neutral--30;

    > header.sam-accordion__header {
      @extend .py-4, .px-6;
    }

    > section.sam-accordion__visible-info {
      @extend .px-6, .pb-4;
    }

    > main > .sam-accordion__content {
      @extend .px-6, .pb-4;
    }
  }

  &.is-flat {
    border: none;

    > header.sam-accordion__header {
      @extend .p-0;
    }

    > main > .sam-accordion__content {
      @extend .px-0, .pt-4, .pb-0;
    }
  }
}
</style>
